import {
	section, h2, p, div, form, label, input, select, option, textarea, button
} from 'iblokz-snabbdom-helpers';
import {fn} from 'iblokz-data';
import dayjs from 'dayjs';
import 'dayjs/locale/bg';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import request from 'superagent';

import {resolveLocalized, DEFAULT_LOCALE} from '../../util/resolve-localized';
import eventTime from '../../util/event-time';

dayjs.extend(localizedFormat);

const pageCopy = (i18n, key, fallback) => {
	const root = i18n && i18n.pages && i18n.pages.contact;
	if (!root || root[key] == null || root[key] === '') return fallback;
	return root[key];
};

const isUpcoming = event => {
	const end = (event && event.endDateTime) || (event && event.startDateTime);
	return new Date(end || 0).getTime() >= Date.now();
};

const isPublished = event => !event.status || event.status === 'published';

const eventLabel = (event, lang) => {
	const title = resolveLocalized(event && event.title, lang)
		|| pageCopy({pages: {contact: {}}}, 'untitled', 'Event');
	const tz = (event && event.timezone) || eventTime.DEFAULT_TZ;
	const start = eventTime.parse(event && event.startDateTime, tz);
	const when = start && start.isValid()
		? start.locale(lang === 'en' ? 'en' : 'bg').format('ll HH:mm')
		: '';
	return when ? `${title} — ${when}` : title;
};

const upcomingFrom = list => (list || [])
	.filter(ev => ev && isPublished(ev) && isUpcoming(ev))
	.slice()
	.sort((a, b) => new Date(a.startDateTime || 0) - new Date(b.startDateTime || 0));

/** Deep-link prefill: /contact?reason=event&event=<slug-or-id> */
const contactQuery = () => {
	if (typeof location === 'undefined' || !location.search) {
		return {reason: '', event: ''};
	}
	const qs = new URLSearchParams(location.search);
	return {
		reason: String(qs.get('reason') || '').trim(),
		event: String(qs.get('event') || '').trim()
	};
};

const matchUpcoming = (upcoming, key) => {
	const k = String(key || '').trim();
	if (!k) return null;
	return (upcoming || []).find(ev =>
		String(ev._id) === k || String(ev.slug || '') === k
	) || null;
};

export default ({state, actions, i18n}) => fn.pipe(
	() => {
		const q = contactQuery();
		const upcoming = upcomingFrom((state.events && state.events.list) || []);
		const contact = state.contact || {};
		const eventKey = contact.event || q.event || '';
		const matched = matchUpcoming(upcoming, eventKey);
		const reason = contact.reason
			|| (q.reason === 'event' || q.reason === 'general' ? q.reason : '')
			|| (q.event ? 'event' : '')
			|| 'general';
		return {
			lang: state.lang || DEFAULT_LOCALE,
			reason,
			eventKey,
			selectedEventId: matched ? String(matched._id) : '',
			sent: !!contact.sent,
			error: contact.error || '',
			sending: !!contact.sending,
			// events list is loaded by resource.hook for /contact (publicPathForNs)
			upcoming
		};
	},
	({lang, reason, eventKey, selectedEventId, sent, error, sending, upcoming}) => section('.contact', [
		h2(pageCopy(i18n, 'title', 'Contact')),
		div([].concat(
			pageCopy(i18n, 'intro', '')
				? p('.contact-intro', pageCopy(i18n, 'intro', ''))
				: [],
				sent
				? p('.contact-thanks', pageCopy(i18n, 'thanks', 'Thanks — we will get back to you.'))
				: form('.edit.contact-form', {
					on: {
						submit: ev => {
							ev.preventDefault();
							if (sending) return;
							// FormData — reliable with snabbdom; formUtil/toData can miss
							// live DOM values when inputs were patched via props.value.
							const formEl = ev.currentTarget || ev.target;
							const fd = new FormData(formEl);
							const data = {
								name: String(fd.get('name') || '').trim(),
								email: String(fd.get('email') || '').trim(),
								reason: String(fd.get('reason') || 'general'),
								event: String(fd.get('event') || ''),
								message: String(fd.get('message') || '').trim()
							};
							actions.set(['contact', 'sending'], true);
							actions.set(['contact', 'error'], '');
							request.post('/api/contact')
								.send(data)
								.then(() => {
									actions.set(['contact'], {
										reason: 'general',
										sent: true,
										sending: false,
										error: ''
									});
								})
								.catch(err => {
									const msg = (err && err.response && err.response.body && err.response.body.err)
										|| pageCopy(i18n, 'error', 'Could not send. Please try again.');
									actions.set(['contact', 'sending'], false);
									actions.set(['contact', 'error'], msg);
								});
						}
					}
				}, [].concat(
					div('.field', [
						label([pageCopy(i18n, 'name', 'Name'), ': ']),
						// Put `required` in attrs — boolean `[required]` in the selector is not
						// parsed by iblokz-snabbdom-helpers and breaks h() arg order (name never set).
						input({
							attrs: {
								type: 'text',
								name: 'name',
								required: 'required',
								autocomplete: 'name'
							}
						})
					]),
					div('.field', [
						label([pageCopy(i18n, 'email', 'Email'), ': ']),
						input({
							attrs: {
								type: 'email',
								name: 'email',
								required: 'required',
								autocomplete: 'email'
							}
						})
					]),
					div('.field', [
						label([pageCopy(i18n, 'reason', 'Reason'), ': ']),
						select({
							attrs: {name: 'reason'},
							on: {
								change: ev => actions.set(['contact', 'reason'], ev.target.value)
							}
						}, [
							option({
								attrs: {value: 'general', selected: reason === 'general'}
							}, pageCopy(i18n, 'reasonGeneral', 'General')),
							option({
								attrs: {value: 'event', selected: reason === 'event'}
							}, pageCopy(i18n, 'reasonEvent', 'Event enquiry'))
						])
					]),
					reason === 'event'
						? [].concat(
							div('.field', [
								label([pageCopy(i18n, 'event', 'Event'), ': ']),
								select('.contact-event-select', {
									key: `contact-event-${upcoming.map(e => e._id).join('-') || 'empty'}-${selectedEventId || eventKey || 'none'}`,
									attrs: {name: 'event', required: 'required'},
									on: {
										change: ev => actions.set(['contact', 'event'], ev.target.value)
									}
								}, [].concat(
									option({
										attrs: {
											value: '',
											selected: !selectedEventId
										}
									},
										upcoming.length
											? pageCopy(i18n, 'eventPlaceholder', 'Select an upcoming event…')
											: pageCopy(i18n, 'eventEmpty', 'No upcoming events')),
									upcoming.map(ev =>
										option({
											key: String(ev._id),
											attrs: {
												value: String(ev._id),
												selected: selectedEventId === String(ev._id)
											}
										}, eventLabel(ev, lang))
									)
								))
							]),
							upcoming.length === 0
								? p('.contact-hint', pageCopy(i18n, 'eventEmptyHint',
									'No published upcoming events right now.'))
								: []
						)
						: [],
					div('.field', [
						label([pageCopy(i18n, 'message', 'Message'), ': ']),
						textarea({
							attrs: {name: 'message', required: 'required', rows: '6'}
						})
					]),
					error ? p('.contact-error', error) : [],
					div('.buttons', [
						button('.btn.btn-primary', {
							attrs: {type: 'submit'},
							props: {disabled: sending}
						}, sending
							? pageCopy(i18n, 'sending', 'Sending…')
							: pageCopy(i18n, 'send', 'Send'))
					])
				))
		))
	])
)();
